import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { MissionType } from '@/lib/mission-type';

/**
 * WHICH MISSION TYPES CAN CARRY CONTINUOUS COVERAGE, according to the server.
 *
 * `POST /api/coverage` refuses every type but the ones this endpoint names, with a reason - today
 * that is HOVER alone, and the endpoint exists precisely so the client does not have to hardcode
 * that and then be wrong the day it changes.
 *
 * NULL IS "NOT KNOWN YET", AND IT IS NOT AN EMPTY LIST. The difference decides what compose does
 * with its offer: an empty list means the server says nothing can hold a station, and the checkbox
 * is genuinely wrong to show; null means nobody has answered, and hiding it would silently drop a
 * capability because a fetch was slow. Both callers treat it that way - the wizard's own
 * `capableTypes` prop carries the same three-state contract and documents it as "the server's
 * allow-list, or null while it is unknown".
 *
 * Read ONCE per COP session rather than polled: this is configuration, not state. It changes when
 * the platform ships a new mission type, not while an operator watches.
 */
/** How long before an unanswered capability read is tried again. */
const CAPABILITY_RETRY_MS = 30_000;

/** How long a single capability read may take before it is abandoned. */
const CAPABILITY_TIMEOUT_MS = 8_000;

/**
 * How many times a failed read is retried before this gives up for the session.
 *
 * BECAUSE "RETRY UNTIL IT LANDS" HAS NO END WHEN THE FAILURE IS PERMANENT. This endpoint returns a
 * static list; the realistic failures are a dead session (a 401 the client's refresh could not
 * rescue) and a malformed body, and neither gets better by asking again. An uncapped schedule meant
 * a COP left open overnight issued one request and one console warning every 30 s until the tab was
 * closed - about 2,880 a day, per tab, against the shared API. Three orders of magnitude smaller
 * than the loop this hook was just fixed for, and the same family of mistake.
 *
 * Four buys two minutes of transient-blip tolerance, which is what the retry was actually for.
 */
const MAX_CAPABILITY_RETRIES = 4;

/**
 * Is this the answer we already hold?
 *
 * Not cosmetic. Writing an equal-but-new array on every response is what turned this hook into an
 * infinite request loop, and comparing by value is what makes it impossible to do so again - the
 * state simply stops changing once the answer stops changing, whatever any future dependency array
 * happens to list.
 */
function sameList(a: MissionType[] | null, b: MissionType[]): boolean {
  return a !== null && a.length === b.length && a.every((t, i) => t === b[i]);
}

export function useCoverageCapability(): { capableTypes: MissionType[] | null } {
  const [capableTypes, setCapableTypes] = useState<MissionType[] | null>(null);
  /**
   * FAILURES ONLY. Incremented in the `catch` and nowhere else, so it is the retry schedule and
   * nothing more: one failure buys exactly one more read, a success buys none.
   *
   * The retry used to be driven off `capableTypes`, which is the answer itself - and an answer that
   * is re-fetched whenever it arrives is a loop. See the header on the effects below.
   */
  const [failures, setFailures] = useState(0);

  const refresh = useCallback(() => {
    api
      .get<{ data: { capable_types: MissionType[] } }>('/api/coverage/capability', {
        signal: AbortSignal.timeout(CAPABILITY_TIMEOUT_MS),
      })
      .then((r) => {
        // BY VALUE, NOT BY IDENTITY. `r.data.capable_types` is a fresh array on every response, so
        // an unconditional write changed the state on a read that changed nothing.
        setCapableTypes((prev) => (sameList(prev, r.data.capable_types) ? prev : r.data.capable_types));
      })
      .catch((err: unknown) => {
        // LEFT AT NULL, never set to []. A failed read must not be drawn as "nothing can hold a
        // station" - that is the same conflation of "we could not ask" with an answer that the
        // `degraded` flag next door exists to prevent, and here it would quietly remove a
        // capability from the operator's reach with nothing on screen to explain it.
        console.warn('Failed to read coverage capability for COP', err);
        setFailures((n) => n + 1);
      });
  }, []);

  /**
   * ONE READ ON MOUNT. Its dependency list holds nothing this hook writes.
   *
   * The previous version called `refresh()` unconditionally and listed `capableTypes` as a
   * dependency, while `setCapableTypes` wrote a new array identity on every response. Response
   * wrote new identity, identity re-ran the effect, effect fetched again: measured on dev at about
   * two requests per second, per open COP, for as long as the tab stayed open - against the API the
   * whole team shares. The `if (capableTypes !== null) return` guard read as though it stopped
   * this and did not; it sat BELOW the fetch and only ever skipped the retry timer.
   *
   * It was invisible in the browser because the endpoint answers 304, so nothing on the page
   * looked wrong and no unit test could see it - the loop only closes over a real round trip,
   * which is why `useCoverageCapability.test.tsx` asserts a CALL COUNT rather than a return value.
   */
  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * RETRIED UNTIL IT LANDS, and driven by the failure count alone.
   *
   * One read is not enough: this value gates whether "hold this station" is offered at all, and a
   * single blip at page load removed that capability for the rest of an operator's shift, silently,
   * with nothing on screen distinguishing "this deployment cannot hold stations" from "we failed to
   * ask once". Backing off rather than hammering - the answer is not urgent, only eventual.
   */
  useEffect(() => {
    if (failures === 0 || failures > MAX_CAPABILITY_RETRIES) return;
    const timer = setTimeout(refresh, CAPABILITY_RETRY_MS);
    return () => clearTimeout(timer);
  }, [refresh, failures]);

  return { capableTypes };
}
